/**
 * Shoji class for managing dynamic sliding content overlays.
 * 
 * This class implements a modern approach to handling sliding overlays, inspired by Japanese shoji doors.
 */
export class Shoji {
  /**
   * Creates a new Shoji instance.
   * @param {Object} options - Configuration options
   * @param {string} [options.urlAttribute='data-shoji'] - Attribute for marking trigger elements
   * @param {string} [options.loadingSVG=null] - Custom SVG for loading spinner
   * @param {Function} [options.onContentLoaded=null] - Callback function to run after content is loaded
   */
  constructor({
    urlAttribute = 'data-shoji',
    loadingSVG = null,
    onContentLoaded = null,
  } = {}) {
    this.urlAttribute = urlAttribute;
    this.loadingSVG = loadingSVG ?? this.getDefaultLoadingSVG();
    this.shojiTriggers = new Map();
    this.shojiElement = null;
    this.onContentLoaded = onContentLoaded;
    this.handlePopState = this.handlePopState.bind(this);
  }

  /**
   * Initializes the Shoji functionality within a container.
   * Sets up event listeners for trigger elements and handles initial URL hash.
   * @param {HTMLElement} [container=document.body] - The container to search for trigger elements
   */
  init(container = document.body) {
    // Query all trigger elements and set up event listeners
    container.querySelectorAll(`[${this.urlAttribute}]`).forEach((link) => {
      this.injectLoadingSpinner(link);
      link.addEventListener('click', this.handleShojiOpen.bind(this));

      // Store trigger elements with their corresponding URL hashes
      const url = new URL(link.getAttribute('href'), window.location.origin);
      this.shojiTriggers.set(url.hash, link);
    });

    // Handle any initial hash in the URL
    this.handleInitialHash();

    // Add popstate event listener
    window.addEventListener('popstate', this.handlePopState);
  }

  /**
   * Handles the case where the page loads with a hash in the URL.
   * Simulates a click on the corresponding trigger element if found.
   */
  handleInitialHash() {
    const hash = window.location.hash;
    const link = this.shojiTriggers.get(hash);
    if (link) {
      this.handleShojiOpen({ preventDefault: () => {}, currentTarget: link });
    }
  }

  /**
   * Handles popstate events for back/forward navigation.
   * @param {PopStateEvent} event - The popstate event
   */
  handlePopState(event) {
    const hash = window.location.hash;
    const link = this.shojiTriggers.get(hash);
    
    if (link) {
      this.handleShojiOpen({ preventDefault: () => {}, currentTarget: link });
    } else if (this.shojiElement) {
      this.closeShoji();
    }
  }

  /**
   * Injects a loading spinner into a trigger element.
   * @param {HTMLElement} link - The trigger element
   */
  injectLoadingSpinner(link) {
    const spinner = document.createElement('span');
    spinner.className = 'shoji-spinner';
    spinner.innerHTML = this.loadingSVG;
    spinner.style.visibility = 'hidden';
    spinner.style.opacity = 0;
    link.appendChild(spinner);
  }

  /**
   * Handles the opening of the shoji overlay.
   * Fetches content, updates URL, and creates the shoji overlay.
   * @param {Event} e - The click event
   */
  async handleShojiOpen(e) {
    // Check if the click event includes a modifier key (⌘ on macOS, Ctrl on others)
    if (e.metaKey || e.ctrlKey) {
      // If a modifier key is pressed, let the default behavior happen (open in new tab)
      return;
    }

    e.preventDefault();
    const link = e.currentTarget;
    const url = link.getAttribute(this.urlAttribute);

    if (!url) {
      console.error(`No URL found. Ensure the ${this.urlAttribute} attribute is set.`);
      return;
    }

    try {
      this.toggleLoadingSpinner(link, true);
      const response = await fetch(`${url}?ajax=true`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const html = await response.text();

      // Ensure the clicked element is in view
      if (typeof document.body.scrollIntoViewIfNeeded === 'function') {
        link.scrollIntoViewIfNeeded();
      }

      this.toggleLoadingSpinner(link, false);
      history.pushState({ shojiOpen: true }, null, link.getAttribute('href'));

      this.createShoji(html, link);
      this.openShoji();
    } catch (error) {
      console.error('Error opening shoji:', error);
      this.toggleLoadingSpinner(link, false);
    }
  }

  /**
   * Toggles the visibility of the loading spinner.
   * @param {HTMLElement} link - The trigger element
   * @param {boolean} show - Whether to show or hide the spinner
   */
  toggleLoadingSpinner(link, show) {
    const spinner = link.querySelector('.shoji-spinner');
    if (spinner) {
      spinner.style.visibility = show ? 'inherit' : 'hidden';
      spinner.style.opacity = show ? 1 : 0;
    }
  }

  /**
   * Creates the shoji overlay element and populates it with content.
   * Sets up event listeners for closing the overlay and executes the onContentLoaded callback.
   * @param {string} html - The HTML content for the overlay
   * @param {HTMLElement} link - The trigger element
   */
  createShoji(html, link) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
  
    this.shojiElement = document.createElement('dialog');
    this.shojiElement.className = 'shoji';
    this.shojiElement.setAttribute('autofocus', '');
    this.shojiElement.innerHTML = `
      <div class="shoji__content">${doc.body.innerHTML}</div>
    `;
  
    document.body.appendChild(this.shojiElement);
  
    this.shojiElement.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // Set up close button functionality
    const closeButton = this.shojiElement.querySelector('[data-close]');
    if (closeButton) {
      closeButton.addEventListener('click', this.closeShoji.bind(this));
    } else {
      console.warn('No element with [data-close] attribute found in the fetched content.');
    }
  
    // Run the onContentLoaded callback if provided, passing the shojiElement and link
    if (typeof this.onContentLoaded === 'function') {
      this.onContentLoaded(this.shojiElement, link);
    }
  }  

  /**
   * Opens the shoji overlay using the dialog API.
   */
  openShoji() {
    this.shojiElement.showModal();
  }

  /**
   * Handles clicks outside the shoji content to close it.
   * @param {Event} e - The click event
   */
  handleOutsideClick(e) {
    if (e.target === this.shojiElement) {
      this.closeShoji();
    }
  }

  /**
   * Closes the shoji overlay, updates the URL, and removes the overlay from the DOM.
   */
  closeShoji() {
    const canonicalUrl = document.querySelector("link[rel='canonical']")?.href;
    history.pushState({ shojiOpen: false }, null, canonicalUrl || '/');

    this.shojiElement.close();

    // Remove the shoji element from the DOM after closing
    this.shojiElement.addEventListener('transitionend', () => {
      this.shojiElement?.remove();
      this.shojiElement = null;
    });
  }

  /**
   * Provides a default SVG for the loading spinner.
   * @returns {string} The SVG markup for the default loading spinner
   */
  getDefaultLoadingSVG() {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" opacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 12 12"
            to="360 12 12"
            dur="1s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    `;
  }
}
